import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  Send, 
  User, 
  Bot, 
  PhoneCall,
  PhoneOff,
  MessageSquare,
  Volume2,
  VolumeX,
  Settings,
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
  BookOpen,
  ChevronDown,
  Radio,
  Square
} from 'lucide-react';
import apiClient from '../../services/api';
import { API_ENDPOINTS } from '../../config/api.config';
import toast from 'react-hot-toast';
import { courseService } from '../../services/course.service';
import { learningAssistantService } from '../../services/learning-assistant.service';
import avatarChatService from '../../services/avatarChatService';
import AvatarDiagnostics from '../../utils/avatarDiagnostics';
import { getVoiceForLanguage } from '../../config/d-id.config';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  mode: 'text' | 'voice' | 'avatar';
  suggestions?: string[];
  contextInfo?: {
    course_title: string;
    current_position: string;
  };
  audioUrl?: string;
  isTranscription?: boolean;
}

interface AvatarSession {
  session_id: string;
  stream_id: string;
  status: string;
  connection_state: string;
}

const LiveChat: React.FC = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  
  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [interactionMode, setInteractionMode] = useState<'text' | 'voice' | 'avatar'>('text');
  const [avatarSession, setAvatarSession] = useState<AvatarSession | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
  const [courses, setCourses] = useState<any[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioPermission, setAudioPermission] = useState<boolean | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [audioOutputMuted, setAudioOutputMuted] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  
  // Language options with proper dialects
  const languageOptions = [
    { code: 'en-US', name: 'English (United States)', flag: '🇺🇸' },
    { code: 'en-GB', name: 'English (United Kingdom)', flag: '🇬🇧' },
    { code: 'en-IN', name: 'English (India)', flag: '🇮🇳' },
    { code: 'en-AU', name: 'English (Australia)', flag: '🇦🇺' },
    { code: 'en-CA', name: 'English (Canada)', flag: '🇨🇦' },
    { code: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
    { code: 'es-MX', name: 'Spanish (Mexico)', flag: '🇲🇽' },
    { code: 'es-US', name: 'Spanish (United States)', flag: '🇺🇸' },
    { code: 'fr-FR', name: 'French (France)', flag: '🇫🇷' },
    { code: 'fr-CA', name: 'French (Canada)', flag: '🇨🇦' },
    { code: 'de-DE', name: 'German (Germany)', flag: '🇩🇪' },
    { code: 'it-IT', name: 'Italian (Italy)', flag: '🇮🇹' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
    { code: 'pt-PT', name: 'Portuguese (Portugal)', flag: '🇵🇹' },
    { code: 'nl-NL', name: 'Dutch (Netherlands)', flag: '🇳🇱' },
    { code: 'ru-RU', name: 'Russian (Russia)', flag: '🇷🇺' },
    { code: 'ja-JP', name: 'Japanese (Japan)', flag: '🇯🇵' },
    { code: 'ko-KR', name: 'Korean (South Korea)', flag: '🇰🇷' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
    { code: 'zh-TW', name: 'Chinese (Traditional)', flag: '🇹🇼' },
    { code: 'ar-SA', name: 'Arabic (Saudi Arabia)', flag: '🇸🇦' },
    { code: 'hi-IN', name: 'Hindi (India)', flag: '🇮🇳' },
    { code: 'ta-IN', name: 'Tamil (India)', flag: '🇮🇳' },
    { code: 'te-IN', name: 'Telugu (India)', flag: '🇮🇳' },
    { code: 'bn-IN', name: 'Bengali (India)', flag: '🇮🇳' },
    { code: 'mr-IN', name: 'Marathi (India)', flag: '🇮🇳' },
    { code: 'gu-IN', name: 'Gujarati (India)', flag: '🇮🇳' },
    { code: 'kn-IN', name: 'Kannada (India)', flag: '🇮🇳' },
    { code: 'ml-IN', name: 'Malayalam (India)', flag: '🇮🇳' },
    { code: 'pa-IN', name: 'Punjabi (India)', flag: '🇮🇳' },
    { code: 'tr-TR', name: 'Turkish (Turkey)', flag: '🇹🇷' },
    { code: 'pl-PL', name: 'Polish (Poland)', flag: '🇵🇱' },
    { code: 'sv-SE', name: 'Swedish (Sweden)', flag: '🇸🇪' },
    { code: 'da-DK', name: 'Danish (Denmark)', flag: '🇩🇰' },
    { code: 'no-NO', name: 'Norwegian (Norway)', flag: '🇳🇴' },
    { code: 'fi-FI', name: 'Finnish (Finland)', flag: '🇫🇮' },
    { code: 'cs-CZ', name: 'Czech (Czech Republic)', flag: '🇨🇿' },
    { code: 'hu-HU', name: 'Hungarian (Hungary)', flag: '🇭🇺' },
    { code: 'el-GR', name: 'Greek (Greece)', flag: '🇬🇷' },
    { code: 'he-IL', name: 'Hebrew (Israel)', flag: '🇮🇱' },
    { code: 'th-TH', name: 'Thai (Thailand)', flag: '🇹🇭' },
    { code: 'vi-VN', name: 'Vietnamese (Vietnam)', flag: '🇻🇳' },
    { code: 'id-ID', name: 'Indonesian (Indonesia)', flag: '🇮🇩' },
    { code: 'ms-MY', name: 'Malay (Malaysia)', flag: '🇲🇾' },
    { code: 'fil-PH', name: 'Filipino (Philippines)', flag: '🇵🇭' },
    { code: 'uk-UA', name: 'Ukrainian (Ukraine)', flag: '🇺🇦' },
    { code: 'ro-RO', name: 'Romanian (Romania)', flag: '🇷🇴' },
    { code: 'bg-BG', name: 'Bulgarian (Bulgaria)', flag: '🇧🇬' },
    { code: 'hr-HR', name: 'Croatian (Croatia)', flag: '🇭🇷' },
    { code: 'sk-SK', name: 'Slovak (Slovakia)', flag: '🇸🇰' },
    { code: 'sl-SI', name: 'Slovenian (Slovenia)', flag: '🇸🇮' }
  ];
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Initialize audio element
  useEffect(() => {
    audioElementRef.current = new Audio();
    audioElementRef.current.addEventListener('ended', handleAudioEnded);
    
    return () => {
      audioElementRef.current?.removeEventListener('ended', handleAudioEnded);
      audioElementRef.current?.pause();
    };
  }, []);
  
  // Check audio permissions on mount
  useEffect(() => {
    checkAudioPermission();
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);
  
  // Load courses on mount
  useEffect(() => {
    loadCourses();
  }, []);
  
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCourseDropdown && !(event.target as Element).closest('.course-dropdown-container')) {
        setShowCourseDropdown(false);
      }
      if (showLanguageDropdown && !(event.target as Element).closest('.language-dropdown-container')) {
        setShowLanguageDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCourseDropdown, showLanguageDropdown]);
  
  const loadCourses = async () => {
    setIsLoadingCourses(true);
    try {
      // First try to get published courses
      let response = await courseService.getMinimalCourses({
        page: 1,
        limit: 100,
        stage: 'published'
      });
      
      
      // If no published courses, try getting all courses
      if (!response || !response.courses || response.courses.length === 0) {
        response = await courseService.getMinimalCourses({
          page: 1,
          limit: 100
          // No stage filter - get all courses
        });
      }
      
      // The service returns the data directly when successful
      if (response && response.courses && response.courses.length > 0) {
        setCourses(response.courses);
        // Select first course by default if available
        setSelectedCourseId(response.courses[0]._id);
      } else {
        setCourses([]);
        toast.info('No courses available. Please create a course first.');
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      // Don't show error toast if it's just no courses
      setCourses([]);
    } finally {
      setIsLoadingCourses(false);
    }
  };
  
  // Audio permission check
  const checkAudioPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setAudioPermission(true);
    } catch (error) {
      console.error('Audio permission denied:', error);
      setAudioPermission(false);
    }
  };
  
  // Start audio recording
  const startRecording = async () => {
    if (!audioPermission) {
      toast.error('Microphone permission denied. Please enable microphone access.');
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      audioStreamRef.current = stream;
      
      // Create MediaRecorder with proper mime type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/ogg';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processAudioRecording(audioBlob);
        audioChunksRef.current = [];
      };
      
      recorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      // Start recording timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
      setAudioPermission(false);
    }
  };
  
  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      
      setIsRecording(false);
      setIsPaused(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };
  
  // Toggle pause/resume recording
  const togglePauseRecording = () => {
    if (!mediaRecorderRef.current) return;
    
    if (isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };
  
  // Process recorded audio
  const processAudioRecording = async (audioBlob: Blob) => {
    // Generate session ID if not present
    let sessionId = conversationId;
    if (!sessionId || sessionId === 'new') {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      navigate(`/learn/live-chat/${sessionId}`, { replace: true });
    }
    
    setIsProcessingAudio(true);
    
    try {
      // Convert blob to file
      const audioFile = new File([audioBlob], 'recording.webm', { 
        type: audioBlob.type || 'audio/webm' 
      });
      
      // Send audio to backend with selected language (one language for everything)
      const response = await learningAssistantService.sendAudioMessage({
        audio_file: audioFile,
        session_id: sessionId,
        course_id: selectedCourseId || '',
        return_audio: true,
        language: selectedLanguage || 'en-US'
      });
      
      // Handle the response - check if it's wrapped in data property
      const responseData = response.data || response;
      
      // Add user transcription message if available
      // The transcription might be in the response as user_transcript or input_text
      const userTranscript = responseData.user_transcript || responseData.input_text || responseData.transcribed_text;
      if (userTranscript) {
        const userMessage: Message = {
          id: `msg_${Date.now()}_user`,
          role: 'user',
          content: userTranscript,
          timestamp: new Date(),
          mode: 'voice',
          isTranscription: true
        };
        setMessages(prev => [...prev, userMessage]);
      } else {
        // If no transcription, add a placeholder message
        const userMessage: Message = {
          id: `msg_${Date.now()}_user`,
          role: 'user',
          content: '[Audio message sent]',
          timestamp: new Date(),
          mode: 'voice',
          isTranscription: false
        };
        setMessages(prev => [...prev, userMessage]);
      }
      
      // Add assistant response
      const assistantMessage: Message = {
        id: responseData.session_id || `msg_${Date.now()}`,
        role: 'assistant',
        content: responseData.response_text || 'I understand. How else can I help you?',
        timestamp: new Date(),
        mode: 'voice',
        audioUrl: responseData.audio_url,
        suggestions: responseData.suggestions,
        contextInfo: responseData.context_info ? {
          course_title: responseData.context_info.course_title,
          current_position: responseData.context_info.current_position || 
            (responseData.context_info.module_title && responseData.context_info.scene_title ? 
              `${responseData.context_info.module_title} - ${responseData.context_info.scene_title}` : '')
        } : undefined
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Play audio response if available
      if (responseData.audio_url && !audioOutputMuted) {
        audioQueueRef.current.push(responseData.audio_url);
        playNextAudio();
      }
      
    } catch (error: any) {
      console.error('Error processing audio:', error);
      // Show more specific error message
      const errorMessage = error.response?.data?.error?.error_description || 
                          error.response?.data?.description || 
                          error.message || 
                          'Failed to process audio recording';
      toast.error(errorMessage);
    } finally {
      setIsProcessingAudio(false);
    }
  };
  
  // Play audio from queue
  const playNextAudio = () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;
    
    const audioUrl = audioQueueRef.current.shift();
    if (!audioUrl || !audioElementRef.current) return;
    
    isPlayingRef.current = true;
    audioElementRef.current.src = audioUrl;
    audioElementRef.current.volume = audioOutputMuted ? 0 : 1;
    audioElementRef.current.play().catch(error => {
      console.error('Error playing audio:', error);
      isPlayingRef.current = false;
      playNextAudio();
    });
  };
  
  // Handle audio playback ended
  const handleAudioEnded = () => {
    isPlayingRef.current = false;
    playNextAudio();
  };
  
  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Initialize conversation
  useEffect(() => {
    if (!conversationId) {
      // Create new conversation if not provided
      createNewConversation();
    } else if (conversationId !== 'new') {
      // Only load history if it's not a new conversation
      // Check if this looks like an existing session ID
      if (conversationId.startsWith('session_')) {
        // This is a new session we just created, don't try to load history
      } else {
        // This might be an existing session, try to load history
        loadConversation(conversationId);
      }
    }
    
    return () => {
      // Cleanup on unmount
      if (avatarSession) {
        endAvatarSession();
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [conversationId]);
  
  const createNewConversation = async () => {
    try {
      // Generate a unique session ID for this conversation
      const session_id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Store session in state instead of calling backend
      setAvatarSession(prev => ({
        ...prev,
        session_id,
        stream_id: '',
        status: 'INITIALIZING',
        connection_state: 'new'
      }));
      
      // Add a welcome message
      const welcomeMessage: Message = {
        id: `msg_welcome_${Date.now()}`,
        role: 'assistant',
        content: 'Hello! I\'m your AI learning assistant. How can I help you today? You can switch between text, voice, and avatar modes using the buttons above.',
        timestamp: new Date(),
        mode: 'text'
      };
      setMessages([welcomeMessage]);
      
      navigate(`/learn/live-chat/${session_id}`, { replace: true });
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create conversation');
    }
  };
  
  const loadConversation = async (id: string) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.LEARNING_ASSISTANT.CHAT_HISTORY(id));
      const history = response.data;
      
      // Load messages from history
      if (history.result?.messages) {
        setMessages(history.result.messages.map((msg: any) => ({
          id: msg.message_id || `msg_${Date.now()}`,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp || Date.now()),
          mode: msg.output_type || 'text'
        })));
      }
    } catch (error: any) {
      console.error('Error loading conversation:', error);
      // Check if it's a 404 or session not found error
      if (error.response?.status === 404 || error.response?.data?.error?.message?.includes('not found')) {
        // Don't show error, just start with empty messages
        setMessages([]);
      } else if (id && id !== 'new' && !id.startsWith('session_')) {
        // Only show error for real failures, not for new sessions
        toast.error('Failed to load conversation history');
      }
    }
  };
  
  // Avatar session management
  const startAvatarSession = async () => {
    if (!conversationId) return;
    
    setIsConnecting(true);
    setConnectionStatus('connecting');
    
    try {
      // Get voice for selected language
      const voice = getVoiceForLanguage(selectedLanguage);
      
      // Create D-ID avatar session
      const response = await avatarChatService.createSession(
        conversationId,
        undefined, // Use default avatar
        voice,
        selectedLanguage
      );
      
      const { session_id, d_id_stream_id, offer, ice_servers, d_id_session_id } = response;
      
      setAvatarSession({
        session_id,
        stream_id: d_id_stream_id,
        status: 'INITIALIZING',
        connection_state: 'new'
      });
      
      // Setup WebRTC connection with D-ID (offer is required for WebRTC)
      if (offer && offer.sdp) {
        await setupWebRTC(offer, ice_servers, session_id);
      } else {
        console.error('No offer received from D-ID Talks API');
        toast.error('Failed to establish video connection');
        return;
      }
      
      setConnectionStatus('connected');
      toast.success('D-ID Avatar session started');
    } catch (error: any) {
      console.error('Error starting avatar session:', error);
      
      // Provide more detailed error message
      let errorMessage = 'Failed to start avatar session';
      if (error.message) {
        errorMessage = error.message;
      }
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      if (error.response?.status === 401) {
        errorMessage = 'Invalid D-ID API key. Please check your API key.';
        localStorage.removeItem('did_api_key'); // Clear invalid key
      }
      
      toast.error(errorMessage);
      setConnectionStatus('error');
    } finally {
      setIsConnecting(false);
    }
  };
  
  const setupWebRTC = async (offer: any, ice_servers: any[], session_id: string) => {
    try {
      
      // First, get user's media stream (audio and video)
      const userStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      localStreamRef.current = userStream;
      
      // Set up local video preview
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = userStream;
      }
      
      // Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: ice_servers
      });
      
      peerConnectionRef.current = pc;
      
      // Create data channel for stream events (D-ID Talks API requirement)
      const dataChannel = pc.createDataChannel('JanusDataChannel');
      dataChannel.addEventListener('open', () => {
      });
      
      dataChannel.addEventListener('message', (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle stream events
          if (message.type === 'stream/ready') {
            setAvatarSession(prev => prev ? {...prev, status: 'ACTIVE'} : null);
          } else if (message.type === 'stream/started') {
          } else if (message.type === 'stream/done') {
          } else if (message.type === 'stream/error') {
            console.error('Stream error:', message.error);
            toast.error('Avatar stream error');
          }
        } catch (err) {
        }
      });
      
      dataChannel.addEventListener('error', (error) => {
        console.error('Data channel error:', error);
      });
      
      // Add local stream tracks to peer connection
      userStream.getTracks().forEach(track => {
        pc.addTrack(track, userStream);
      });
      
      
      // Handle ICE candidates for D-ID
      pc.onicecandidate = async (event) => {
        if (event.candidate) {
          try {
            await avatarChatService.submitICECandidate(session_id, event.candidate);
          } catch (error) {
            console.error('Error sending ICE candidate:', error);
          }
        }
      };
      
      // Handle connection state changes for D-ID
      pc.onconnectionstatechange = async () => {
        const state = pc.connectionState;
        
        await avatarChatService.updateConnectionState(session_id, state);
        
        setConnectionStatus(state);
        
        if (state === 'connected') {
          setAvatarSession(prev => prev ? {...prev, connection_state: 'connected', status: 'ACTIVE'} : null);
        } else if (state === 'failed' || state === 'disconnected') {
          toast.error('Avatar connection lost');
        }
      };
      
      // Handle remote stream
      pc.ontrack = (event) => {
        if (event.streams && event.streams[0]) {
          remoteStreamRef.current = event.streams[0];
          if (videoRef.current) {
            videoRef.current.srcObject = event.streams[0];
          }
        }
      };
      
      // Set remote description (offer)
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      // Send answer to D-ID server
      await avatarChatService.submitSDPAnswer(session_id, answer);
    } catch (error) {
      console.error('Error setting up WebRTC:', error);
      if (error instanceof Error && error.name === 'NotAllowedError') {
        toast.error('Camera and microphone access denied. Please enable permissions and try again.');
      } else {
        toast.error('Failed to set up video connection. Please check your camera and microphone.');
      }
      throw error;
    }
  };
  
  const endAvatarSession = async () => {
    if (!avatarSession) return;
    
    try {
      await avatarChatService.endSession(avatarSession.session_id, 'User ended session');
      
      // Close peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // Stop local stream tracks
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      
      // Clear local video element
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
      }
      
      // Reset video and audio states
      setIsVideoEnabled(true);
      setIsAudioEnabled(true);
      
      setAvatarSession(null);
      setConnectionStatus('disconnected');
      toast.info('D-ID Avatar session ended');
    } catch (error) {
      console.error('Error ending avatar session:', error);
    }
  };
  
  // Send message
  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim() || !conversationId) return;
    
    const user_message: Message = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
      mode: interactionMode
    };
    
    setMessages(prev => [...prev, user_message]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      if (interactionMode === 'avatar' && avatarSession?.status === 'ACTIVE') {
        // Send to avatar with course context
        const response = await avatarChatService.sendChatWithContext(
          avatarSession.session_id,
          textToSend,
          selectedCourseId || '',
          conversationId || `session_${Date.now()}`,
          selectedLanguage
        );
        
        // Add assistant response with AI-generated content
        const responseData = response.data || response;
        const assistant_message: Message = {
          id: `avatar_${Date.now()}`,
          role: 'assistant',
          content: responseData.response_text || textToSend, // Avatar will speak the AI response
          timestamp: new Date(),
          mode: 'avatar'
        };
        setMessages(prev => [...prev, assistant_message]);
      } else {
        // Send as regular text message using the learning assistant service with selected language
        const response = await learningAssistantService.sendTextMessage({
          message: textToSend,
          session_id: conversationId || `session_${Date.now()}`,
          course_id: selectedCourseId || '',
          include_audio_response: interactionMode === 'voice',
          language: selectedLanguage || 'en-US'
        });
        
        // Handle the response based on the actual structure
        const responseData = response.data || response;
        const assistant_message: Message = {
          id: responseData.session_id || `msg_${Date.now()}`,
          role: 'assistant',
          content: responseData.response_text || 'I understand. How can I help you learn today?',
          timestamp: new Date(),
          mode: interactionMode,
          audioUrl: responseData.audio_url,
          suggestions: responseData.suggestions || [],
          contextInfo: responseData.context_info ? {
            course_title: responseData.context_info.course_title,
            current_position: `${responseData.context_info.module_title || ''} - ${responseData.context_info.scene_title || ''}`
          } : undefined
        };
        setMessages(prev => [...prev, assistant_message]);
        
        // Play audio if available in voice mode
        if (responseData.audio_url && interactionMode === 'voice' && !audioOutputMuted) {
          audioQueueRef.current.push(responseData.audio_url);
          playNextAudio();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check camera and microphone permissions
  const checkCameraAndMicPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error('Camera/microphone permission denied:', error);
      return false;
    }
  };
  
  // Switch interaction mode
  const switchMode = async (new_mode: 'text' | 'voice' | 'avatar') => {
    if (!conversationId) return;
    
    try {
      // Stop recording if switching away from voice mode
      if (interactionMode === 'voice' && new_mode !== 'voice') {
        stopRecording();
      }
      
      // Request audio permission if switching to voice mode
      if (new_mode === 'voice' && audioPermission === null) {
        await checkAudioPermission();
      }
      
      // Check camera and microphone permissions for avatar mode
      if (new_mode === 'avatar' && !avatarSession) {
        const hasPermissions = await checkCameraAndMicPermissions();
        if (!hasPermissions) {
          toast.error('Camera and microphone access required for avatar mode. Please enable permissions and try again.');
          return;
        }
      }
      
      // No need to call the old LIVE_AVATAR endpoint for D-ID
      // Just set the mode locally
      setInteractionMode(new_mode);
      
      // Start avatar session if switching to avatar mode
      if (new_mode === 'avatar' && !avatarSession) {
        await startAvatarSession();
      }
      
      // End avatar session if switching away from avatar
      if (new_mode !== 'avatar' && avatarSession) {
        await endAvatarSession();
      }

      // Toast message removed as requested
    } catch (error) {
      console.error('Error switching mode:', error);
      toast.error('Failed to switch mode');
    }
  };
  
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const video_track = localStreamRef.current.getVideoTracks()[0];
      if (video_track) {
        video_track.enabled = !isVideoEnabled;
        setIsVideoEnabled(!isVideoEnabled);
        toast.success(`Camera ${!isVideoEnabled ? 'enabled' : 'disabled'}`);
      } else {
        toast.error('No video track available');
      }
    } else {
      toast.error('No camera stream available. Please start avatar session first.');
    }
  };
  
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audio_track = localStreamRef.current.getAudioTracks()[0];
      if (audio_track) {
        audio_track.enabled = !isAudioEnabled;
        setIsAudioEnabled(!isAudioEnabled);
        toast.success(`Microphone ${!isAudioEnabled ? 'enabled' : 'disabled'}`);
      } else {
        toast.error('No audio track available');
      }
    } else {
      toast.error('No microphone stream available. Please start avatar session first.');
    }
  };
  
  // Run diagnostics for debugging
  const runDiagnostics = async () => {
    const results = await AvatarDiagnostics.runFullDiagnostics(
      localStreamRef.current,
      peerConnectionRef.current
    );
    toast.success('Diagnostics completed - check console');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Bot className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-bold text-white">Live Learning Assistant</h1>
                  <p className="text-indigo-200 text-sm">Powered by AI</p>
                </div>
              </div>
              
              {/* Course Selector, Language Selector and Mode Switcher */}
              <div className="flex items-center gap-4">
                {/* Language Dropdown */}
                <div className="relative language-dropdown-container">
                  <button
                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors text-white"
                  >
                    <span className="text-lg">
                      {languageOptions.find(l => l.code === selectedLanguage)?.flag || '🌐'}
                    </span>
                    <span className="text-sm">
                      {languageOptions.find(l => l.code === selectedLanguage)?.name.split(' ')[0] || 'Language'}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {showLanguageDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-xl z-50">
                      <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Select Language & Dialect
                        </div>
                        {languageOptions.map(language => (
                          <button
                            key={language.code}
                            onClick={() => {
                              setSelectedLanguage(language.code);
                              setShowLanguageDropdown(false);
                              toast.success(`Language set to ${language.name}`);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-3 ${
                              selectedLanguage === language.code ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                            }`}
                          >
                            <span className="text-lg">{language.flag}</span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">{language.name}</div>
                              <div className="text-xs text-gray-500">{language.code}</div>
                            </div>
                            {selectedLanguage === language.code && (
                              <CheckCircle className="w-4 h-4 text-indigo-600" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Course Dropdown */}
                <div className="relative course-dropdown-container">
                  <button
                    onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                    disabled={isLoadingCourses}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors text-white"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span className="text-sm">
                      {isLoadingCourses ? 'Loading...' : 
                       selectedCourseId ? courses.find(c => c._id === selectedCourseId)?.title || 'Select Course' : 
                       'Select Course'}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {showCourseDropdown && !isLoadingCourses && (
                    <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-xl z-50">
                      <div className="p-2">
                        {courses.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            No courses available
                          </div>
                        ) : (
                          courses.map(course => (
                            <button
                              key={course._id}
                              onClick={() => {
                                setSelectedCourseId(course._id);
                                setShowCourseDropdown(false);
                                toast.success(`Selected: ${course.title}`);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors ${
                                selectedCourseId === course._id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                              }`}
                            >
                              <div className="font-medium text-gray-900 text-sm">{course.title}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {course.module_count} modules • {course.difficulty}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Mode Switcher */}
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-1">
                <button
                  onClick={() => switchMode('text')}
                  className={`px-4 py-2 rounded-md transition-all ${
                    interactionMode === 'text' 
                      ? 'bg-white text-indigo-600 shadow-lg' 
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                </button>
                <button
                  onClick={() => switchMode('voice')}
                  className={`px-4 py-2 rounded-md transition-all ${
                    interactionMode === 'voice' 
                      ? 'bg-white text-indigo-600 shadow-lg' 
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  <Volume2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => switchMode('avatar')}
                  className={`px-4 py-2 rounded-md transition-all hidden ${
                    interactionMode === 'avatar'
                      ? 'bg-white text-indigo-600 shadow-lg'
                      : 'text-white hover:bg-white/20'
                  }`}
                >
                  <Video className="w-5 h-5" />
                </button>
              </div>
              </div>
            </div>
            
            {/* Status Bar */}
            <div className="mt-4 flex items-center gap-4">
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                interactionMode === 'avatar' ? 'bg-green-100 text-green-800' : 
                interactionMode === 'voice' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-blue-100 text-blue-800'
              }`}>
                <Sparkles className="w-3 h-3" />
                {interactionMode.toUpperCase()} MODE
              </span>
              
              {avatarSession && (
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                  connectionStatus === 'connected' ? 'bg-green-100 text-green-800' :
                  connectionStatus === 'connecting' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    connectionStatus === 'connected' ? 'bg-green-500' :
                    connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                    'bg-red-500'
                  }`} />
                  {connectionStatus.toUpperCase()}
                </span>
              )}
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex h-[600px]">
            {/* Chat Section */}
            <div className={`flex-1 flex flex-col ${interactionMode === 'avatar' ? 'w-3/5' : 'w-full'}`}>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-start gap-3 max-w-[70%] ${
                      message.role === 'user' ? 'flex-row-reverse' : ''
                    }`}>
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        message.role === 'user' 
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-500'
                      }`}>
                        {message.role === 'user' ? (
                          <User className="w-5 h-5 text-white" />
                        ) : (
                          <Bot className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <div className={`rounded-2xl px-4 py-3 ${
                          message.role === 'user' 
                            ? 'bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 text-white' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {message.isTranscription && (
                            <div className={`flex items-center gap-2 mb-2 text-xs ${
                              message.role === 'user' ? 'text-blue-200' : 'text-gray-500'
                            }`}>
                              <Mic className="w-3 h-3" />
                              <span>Transcribed from audio</span>
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          {message.audioUrl && message.role === 'assistant' && (
                            <button
                              onClick={() => {
                                if (audioElementRef.current && message.audioUrl) {
                                  // Stop any current playback
                                  audioElementRef.current.pause();
                                  audioElementRef.current.currentTime = 0;
                                  
                                  // Play the new audio
                                  audioElementRef.current.src = message.audioUrl;
                                  audioElementRef.current.volume = audioOutputMuted ? 0 : 1;
                                  audioElementRef.current.play().catch(err => {
                                    console.error('Error playing audio:', err);
                                    toast.error('Failed to play audio response');
                                  });
                                }
                              }}
                              className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                            >
                              <Volume2 className="w-4 h-4" />
                              <span className="text-xs">Play Audio Response</span>
                            </button>
                          )}
                          {message.contextInfo && message.role === 'assistant' && (
                            <div className="mt-3 pt-2 border-t border-gray-200">
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="inline-flex items-center gap-1">
                                  <BookOpen className="w-3 h-3" />
                                  <span className="font-medium">{message.contextInfo.course_title}</span>
                                </span>
                              </div>
                              {message.contextInfo.current_position && (
                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                  <span className="inline-flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" />
                                    <span>{message.contextInfo.current_position}</span>
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          <div className={`flex items-center gap-2 mt-2 text-xs ${
                            message.role === 'user' ? 'text-blue-200' : 'text-gray-500'
                          }`}>
                            <span>{message.timestamp.toLocaleTimeString()}</span>
                            <span>•</span>
                            <span className="capitalize flex items-center gap-1">
                              {message.mode === 'voice' && <Mic className="w-3 h-3" />}
                              {message.mode === 'avatar' && <Video className="w-3 h-3" />}
                              {message.mode === 'text' && <MessageSquare className="w-3 h-3" />}
                              {message.mode}
                            </span>
                          </div>
                        </div>
                        {message.suggestions && message.suggestions.length > 0 && message.role === 'assistant' && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2 font-medium">Suggested questions:</p>
                            <div className="flex flex-wrap gap-2">
                              {message.suggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    // Auto-send the suggestion
                                    sendMessage(suggestion);
                                  }}
                                  disabled={isLoading}
                                  className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-full hover:bg-indigo-50 hover:border-indigo-300 transition-all text-gray-700 hover:text-indigo-600 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div className="bg-gray-100 rounded-2xl px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                          <span className="text-sm text-gray-600">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input Area */}
              <div className="border-t p-4">
                {interactionMode === 'voice' ? (
                  // Voice mode controls
                  <div className="space-y-4">
                    {/* Recording status */}
                    {isRecording && (
                      <div className="flex items-center justify-between bg-red-50 rounded-lg px-4 py-2">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Radio className="w-5 h-5 text-red-600" />
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                          </div>
                          <span className="text-sm font-medium text-red-600">
                            {isPaused ? 'Recording Paused' : 'Recording'}
                          </span>
                          <span className="text-sm text-gray-600">
                            {formatTime(recordingTime)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Pause/Resume button */}
                          <button
                            onClick={togglePauseRecording}
                            className="p-2 rounded-lg bg-white hover:bg-gray-100 transition-colors"
                            title={isPaused ? 'Resume' : 'Pause'}
                          >
                            {isPaused ? (
                              <Mic className="w-4 h-4 text-gray-600" />
                            ) : (
                              <MicOff className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                          
                          {/* Stop button */}
                          <button
                            onClick={stopRecording}
                            className="p-2 rounded-lg bg-red-100 hover:bg-red-200 transition-colors"
                            title="Stop Recording"
                          >
                            <Square className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Processing indicator */}
                    {isProcessingAudio && (
                      <div className="flex items-center gap-3 bg-blue-50 rounded-lg px-4 py-3">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        <span className="text-sm text-blue-600">Thinking...</span>
                      </div>
                    )}
                    
                    {/* Main controls */}
                    <div className="flex items-center gap-3">
                      {/* Record button */}
                      {!isRecording && !isProcessingAudio && (
                        <button
                          onClick={startRecording}
                          disabled={audioPermission === false}
                          className="flex-1 flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 text-white rounded-xl hover:from-brand-indigo-700 hover:to-brand-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Mic className="w-5 h-5" />
                          <span className="font-medium">
                            {audioPermission === false ? 'Microphone Access Denied' : 'Start Recording'}
                          </span>
                        </button>
                      )}
                      
                      {/* Audio output mute toggle */}
                      <button
                        onClick={() => setAudioOutputMuted(!audioOutputMuted)}
                        className={`p-3 rounded-xl transition-all ${
                          audioOutputMuted
                            ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={audioOutputMuted ? 'Unmute Responses' : 'Mute Responses'}
                      >
                        {audioOutputMuted ? (
                          <VolumeX className="w-5 h-5" />
                        ) : (
                          <Volume2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    
                    {/* Voice mode tips */}
                    {!isRecording && !isProcessingAudio && (
                      <div className="text-xs text-gray-500 text-center">
                        Click the microphone button to start recording your question
                      </div>
                    )}
                  </div>
                ) : (
                  // Text/Avatar mode input
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder={
                        interactionMode === 'avatar' 
                          ? "Type message for avatar to speak..." 
                          : "Type your message..."
                      }
                      disabled={isLoading || (interactionMode === 'avatar' && (!avatarSession || avatarSession.status !== 'ACTIVE'))}
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <button
                      onClick={() => sendMessage()}
                      disabled={isLoading || !inputMessage.trim() || (interactionMode === 'avatar' && (!avatarSession || avatarSession.status !== 'ACTIVE'))}
                      className="p-3 rounded-xl bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 text-white hover:from-brand-indigo-700 hover:to-brand-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Avatar Video Panel */}
            {interactionMode === 'avatar' && (
              <div className="w-2/5 border-l flex flex-col bg-gray-50">
                <div className="p-4 bg-white border-b">
                  <h3 className="text-lg font-semibold text-gray-800">Live Avatar</h3>
                  <p className="text-sm text-gray-500">Interactive AI Assistant</p>
                </div>
                
                <div className="flex-1 relative bg-black">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Local video preview (user's own camera) */}
                  {localStreamRef.current && (
                    <div className="absolute top-4 right-4 w-32 h-24 bg-gray-900 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                      <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover transform scale-x-[-1]"
                      />
                      {!isVideoEnabled && (
                        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                          <VideoOff className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {isConnecting && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <div className="text-center">
                        <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
                        <p className="text-white text-lg font-medium">Connecting to avatar...</p>
                      </div>
                    </div>
                  )}
                  
                  {!avatarSession && !isConnecting && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-indigo-900/90 to-purple-900/90">
                      <div className="text-center">
                        <Video className="w-16 h-16 text-white mx-auto mb-4" />
                        <button
                          onClick={startAvatarSession}
                          className="px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                        >
                          Start Avatar Session
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Avatar Controls */}
                <div className="p-4 bg-white border-t">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={toggleVideo}
                      disabled={!avatarSession || avatarSession.status !== 'ACTIVE'}
                      className={`p-3 rounded-lg transition-all ${
                        !avatarSession || avatarSession.status !== 'ACTIVE'
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : isVideoEnabled 
                            ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200' 
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                      }`}
                      title={!avatarSession ? 'Start avatar session to enable camera control' : isVideoEnabled ? 'Disable Camera' : 'Enable Camera'}
                    >
                      {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={toggleAudio}
                      disabled={!avatarSession || avatarSession.status !== 'ACTIVE'}
                      className={`p-3 rounded-lg transition-all ${
                        !avatarSession || avatarSession.status !== 'ACTIVE'
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                          : isAudioEnabled 
                            ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200' 
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                      }`}
                      title={!avatarSession ? 'Start avatar session to enable microphone control' : isAudioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
                    >
                      {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                    </button>
                    {avatarSession && (
                      <button
                        onClick={endAvatarSession}
                        className="p-3 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all"
                        title="End Avatar Session"
                      >
                        <PhoneOff className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => avatarSession && startAvatarSession()}
                      disabled={!avatarSession}
                      className={`p-3 rounded-lg transition-all ${
                        !avatarSession 
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={!avatarSession ? 'No active session to reconnect' : 'Reconnect Avatar Session'}
                    >
                      <RefreshCw className="w-5 h-5" />
                    </button>
                    {/* Diagnostic button for debugging */}
                    {avatarSession && (
                      <button
                        onClick={runDiagnostics}
                        className="p-3 rounded-lg bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition-all"
                        title="Run Voice Chat Diagnostics"
                      >
                        <Settings className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  
                  {/* Status indicator */}
                  <div className="mt-3 text-center">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                      !avatarSession ? 'bg-gray-100 text-gray-500' :
                      avatarSession.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        !avatarSession ? 'bg-gray-400' :
                        avatarSession.status === 'ACTIVE' ? 'bg-green-500 animate-pulse' :
                        'bg-yellow-500 animate-pulse'
                      }`} />
                      {!avatarSession ? 'Not Connected' : 
                       avatarSession.status === 'ACTIVE' ? 'Live Session Active' : 
                       'Connecting...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveChat;